import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';
import './AcceptInvite.css';

const ROLE_DESCRIPTIONS = {
  ADMIN: 'Full administrative access to settings, billing, team management, and API access.',
  MANAGER: 'Management access to configure API keys, schemas, policies, and view audit trails.',
  SECURITY_OFFICER: 'Security governance access to configure PII masking policies, schemas, and compliance controls.',
  AUDITOR: 'Read-only compliance audit access to logs, compliance dashboards, and policies.',
  USER: 'Developer access to tokenization, detokenization, and LLM gateway APIs.'
};

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided.');
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const res = await fetch(`${API_BASE}/public/team/invitations/validate?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok || !data.valid) {
          throw new Error(data.error || 'Invalid or expired invitation token.');
        }
        setInvitation(data);
        if (user?.displayName) {
          setName(user.displayName);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, user]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/public/team/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          uid: user?.uid || null,
          email: user?.email || invitation?.email,
          name: name.trim() || user?.displayName || invitation?.email.split('@')[0]
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to accept invitation.');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2500);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="accept-invite-container">
      <div className="accept-invite-card">
        <div className="invite-logo-row">
          <span className="invite-brand-icon">🛡️</span>
          <h2>Kryda Cloud Vaultize</h2>
        </div>

        {loading ? (
          <div className="invite-state-loading">
            <span className="spinner">⏳</span>
            <p>Verifying workspace invitation...</p>
          </div>
        ) : error ? (
          <div className="invite-state-error">
            <div className="error-icon">⚠️</div>
            <h3>Invitation Invalid or Expired</h3>
            <p>{error}</p>
            <Link to="/login" className="btn-return-login">
              Return to Login
            </Link>
          </div>
        ) : success ? (
          <div className="invite-state-success">
            <div className="success-icon">🎉</div>
            <h3>Welcome to the Workspace!</h3>
            <p>
              Your membership has been activated as <strong>{invitation?.roleLabel || invitation?.role}</strong>.
              Redirecting to login...
            </p>
          </div>
        ) : (
          <div>
            <div className="invite-welcome-banner">
              <p className="invite-subtitle">You have been invited to join</p>
              <h3 className="workspace-name">{invitation?.workspaceEmail}</h3>
              <div className="workspace-plan-badge">Workspace: {invitation?.workspacePlan}</div>
            </div>

            <div className="role-assignment-box">
              <div className="role-badge-row">
                <span>Assigned Role:</span>
                <span className={`role-pill role-${(invitation?.role || '').toLowerCase()}`}>
                  {invitation?.roleLabel || invitation?.role}
                </span>
              </div>
              <p className="role-description">
                {ROLE_DESCRIPTIONS[invitation?.role] || 'Standard team workspace member privileges.'}
              </p>
            </div>

            <form onSubmit={handleAccept} className="accept-invite-form">
              <div className="form-group">
                <label>Invited Email</label>
                <input
                  type="email"
                  value={invitation?.email || ''}
                  disabled
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Your Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. Sarah Connor"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn-accept-invite"
                disabled={submitting}
              >
                {submitting ? '⏳ Joining Workspace...' : 'Accept Invitation & Join Team'}
              </button>
            </form>

            <div className="invite-footer-note">
              Already have an account? Sign in with <strong>{invitation?.email}</strong> after accepting.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
